import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { X } from 'lucide-react';
import { useEffect, useId, useRef, } from 'react';
import { cn } from './cn.js';
// `left-auto`/`right-auto` are load-bearing: the UA `dialog:modal` rule sets
// `inset: 0`, which would pin the panel to the left even with `right-0`. The
// `[&[open]]:` attribute variant drives the slide reliably (more robust than the
// `open:` variant when scanned out of the built dist).
const SIDE_CLASSES = {
    right: 'right-0 left-auto border-l translate-x-full [&[open]]:translate-x-0 [&[open]]:starting:translate-x-full',
    left: 'left-0 right-auto border-r -translate-x-full [&[open]]:translate-x-0 [&[open]]:starting:-translate-x-full',
};
/**
 * Right/left master-detail drawer built on the native <dialog>. `showModal()`
 * gives a focus trap, Esc-to-close, and an inert background for free; we add a
 * slide on the drawer curve and a warm scrim. Backdrop clicks close it (the
 * click target is the dialog element itself, since content fills the box).
 */
export function Drawer({ open, onClose, title, children, footer, side = 'right', ariaLabel, closeLabel, className, }) {
    const dialogRef = useRef(null);
    const titleId = useId();
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog)
            return;
        if (open && !dialog.open)
            dialog.showModal();
        else if (!open && dialog.open)
            dialog.close();
    }, [open]);
    const onBackdropClick = (e) => {
        if (e.target === dialogRef.current)
            onClose();
    };
    return (_jsx("dialog", { ref: dialogRef, onClose: onClose, onClick: onBackdropClick, "aria-labelledby": title ? titleId : undefined, "aria-label": title ? undefined : ariaLabel, className: cn('fixed inset-y-0 m-0 h-dvh max-h-dvh w-full max-w-md bg-card text-card-foreground shadow-elev-3 outline-none ' +
            'border-border transition-transform duration-[var(--dur-base)] ease-[var(--ease-drawer)] motion-reduce:transition-none ' +
            'backdrop:bg-[var(--overlay)] backdrop:backdrop-blur-sm', SIDE_CLASSES[side], className), children: _jsxs("div", { className: "flex h-full flex-col", children: [_jsxs("div", { className: "flex items-center justify-between gap-3 border-b border-border p-5", children: [title ? (_jsx("h2", { id: titleId, className: "font-display text-lg font-semibold tracking-tight", children: title })) : (_jsx("span", {})), _jsx("button", { type: "button", onClick: onClose, "aria-label": closeLabel, className: "inline-flex size-10 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1", children: _jsx(X, { className: "size-5", "aria-hidden": "true" }) })] }), _jsx("div", { className: "flex-1 overflow-y-auto p-5", children: children }), footer && (_jsx("div", { className: "flex items-center justify-end gap-2 border-t border-border p-5", children: footer }))] }) }));
}
